/**
 * DynamicProvider.jsx
 * Wrapper für Dynamic SDK mit Randalaise-Konfiguration
 *
 * Wenn DYNAMIC_ENV_ID nicht gesetzt ist, wird das Spiel ohne Auth geladen.
 */
import React from 'react';

// MetaMask-Workaround: window.ethereum komplett verstecken BEVOR Dynamic lädt
// Dynamic SDK versucht sonst automatisch eine Verbindung herzustellen,
// was fehlschlägt wenn MetaMask installiert aber gesperrt ist.
// Da wir nur Social Login (Google) nutzen, brauchen wir ethereum gar nicht.
if (typeof window !== 'undefined' && !window.__ethereumHidden) {
  window.__ethereumHidden = true;

  // Original speichern falls später benötigt
  if (window.ethereum) {
    Object.defineProperty(window, '__originalEthereum', {
      value: window.ethereum,
      writable: false,
      configurable: false,
    });

    // ethereum für Dynamic "unsichtbar" machen
    // Wir löschen es nicht komplett, sondern machen es undefined
    try {
      delete window.ethereum;
    } catch {
      // Falls delete nicht funktioniert (manche Browser), überschreiben
      window.ethereum = undefined;
    }

    console.debug('[DynamicProvider] Hidden window.ethereum to prevent MetaMask errors');
  }
}

const DYNAMIC_ENV_ID = process.env.REACT_APP_DYNAMIC_ENV_ID;

// Auth ist optional aktiviert
const AUTH_ENABLED = !!DYNAMIC_ENV_ID && DYNAMIC_ENV_ID.length > 10;

if (!AUTH_ENABLED) {
  console.warn('[Dynamic] Auth deaktiviert - REACT_APP_DYNAMIC_ENV_ID nicht gesetzt');
}

// Lazy-Load Dynamic SDK nur wenn Auth aktiviert ist
const DynamicContextProvider = AUTH_ENABLED
  ? React.lazy(() =>
      import('@dynamic-labs/sdk-react-core').then((mod) => ({
        default: mod.DynamicContextProvider,
      }))
    )
  : null;

// Wallet-Connectors deaktiviert - nur Social Login (Google) wird verwendet
// MetaMask etc. verursacht Fehler wenn installiert aber gesperrt
// eslint-disable-next-line no-unused-vars
const _EthereumWalletConnectors = null;

const dynamicSettings = AUTH_ENABLED
  ? {
      environmentId: DYNAMIC_ENV_ID,

      // Keine Wallet-Connectors - nur Social Login
      walletConnectors: [],

      // Wallet-Erkennung komplett deaktivieren (verhindert MetaMask-Fehler)
      // Muss leeres Array zurückgeben, nicht false
      walletsFilter: () => [],

      // UI-Anpassungen passend zu Randalaise
      cssOverrides: `
        .dynamic-widget-modal {
          --dynamic-font-family: inherit;
          --dynamic-primary-color: #6366f1;
          --dynamic-border-radius: 12px;
        }
      `,

      // Events für Debugging/Analytics
      eventsCallbacks: {
        onAuthSuccess: (args) => {
          console.log('[Dynamic] Auth success:', args.user?.userId);
        },
        onLogout: () => {
          console.log('[Dynamic] User logged out');
        },
      },

      // Embedded Wallets deaktiviert - nicht benötigt für Social Login
      embeddedWallets: {
        createOnLogin: 'off',
      },

      // Zusätzlich: Wallet-Features komplett aus
      walletConnectorsOrder: [],
    }
  : null;

export function DynamicProvider({ children }) {
  // Auth deaktiviert → direkt children rendern
  if (!AUTH_ENABLED) {
    return <>{children}</>;
  }

  // Auth aktiviert → Dynamic SDK laden
  return (
    <React.Suspense
      fallback={
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0a0a1a',
          color: '#fff',
        }}>
          Lade...
        </div>
      }
    >
      <DynamicContextProvider settings={dynamicSettings}>
        {children}
      </DynamicContextProvider>
    </React.Suspense>
  );
}

// Export ob Auth aktiviert ist (für andere Komponenten)
export const isAuthEnabled = AUTH_ENABLED;
